// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';

import type { ModelEditOp, SolidWall } from '../../api/types';
import type { GeometriEditor, GestureStatus } from '../../app/bygning/GeometriEditorContext';
import { applyOps, lengthInput, parseLength } from '../../bygning/geometriEdit';
import { guideSnaps } from '../../bygning/cadMeasure';
import { INFERENCE_TOKEN, infer, manhattanAngle, type Inference, type SnapSegment } from '../../bygning/inference';
import {
  modifyBack,
  modifyClick,
  modifyEnter,
  modifyHint,
  modifyPreview,
  parseAngle,
  typedField,
  type ModifyPreview,
  type ModifySession,
  type PointerPick,
} from '../../bygning/modifyTool';
import type { XY } from '../../bygning/planWalls';
import { chainPlan, isClosed, rectanglePoints, traceAxis, wallAt, type DrawContext } from '../../bygning/wallDraw';
import { angleOf, frame, tempDims, typedEndpoint, type TempDim } from '../../bygning/wallGrips';
import { splitPoint } from '../../bygning/wallModify';
import type { ViewportHandle } from '../../viewport/Viewport';
import { autoTrackPoints, type TrackPoint } from '../../bygning/smartGuides';
import { Glyph, ScreenTip, TrackLines } from './GeometriLayer';
import { useSmartTracking } from './useSmartTracking';
import styles from './GeometriLayer.module.css';

/** The inference engine's reach, px. */
const SNAP_PX = 12;
/** A pick's reach beyond a wall's faces, px (as Vælg's click). */
const PICK_PX = 8;
/** Two clicks this close (px) on the chain's last point end it (a double click). */
const SAME_PX = 4;

export interface CadToolLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  host: RefObject<HTMLElement | null>;
  editor: GeometriEditor;
  storeyId: number | null;
  z: number;
}

interface Mods {
  shift: boolean;
  alt: boolean;
}

/** The pointer as last seen, resolved. */
interface Pointer {
  raw: XY;
  inf: Inference;
  pick: PointerPick;
}

/** SketchUp's measurement box: the typed fields and the one being typed. */
interface Typed {
  fields: string[];
  at: number;
}

const fmt = (v: number) => lengthInput(v);
const fmtDeg = (v: number) => `${v.toFixed(1).replace('.', ',')}°`;
const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);

const WALL_FIELDS = ['Længde', 'Vinkel'] as const;

/**
 * The tool's own tracking points (smart guides): a command's walls as they
 * stand and their neighbours' far ends; the chain's corners and the walls
 * the chain starts or turns on.
 */
function toolTrackPoints(walls: readonly SolidWall[], s: ModifySession | null, chain: readonly XY[], near: XY | null): TrackPoint[] {
  if (s) return autoTrackPoints(walls, s.guids, near);
  if (chain.length === 0) return [];
  const touching = walls.filter((w) => chain.some((p) => dist(w.start, p) < 0.01 || dist(w.end, p) < 0.01)).map((w) => w.guid);
  const corners: TrackPoint[] = chain.map((p, i) => ({ p, what: i === 0 ? 'endepunkt' : 'hjørne', of: 'Ny væg', auto: true }));
  return [...corners, ...autoTrackPoints(walls, touching, near)];
}

/**
 * Rediger › Geometri's drawing and modify tools on the locked plan
 * (frontend-u0o.4, slice S4), beside `GeometriLayer` (selection and grips):
 *
 *  - the Væg tool (W): Linje draws a chain (each click a corner; Esc, Enter
 *    or a second click on the last point ends it, the first point closes
 *    it), Rektangel two opposite corners, Fra planspor a click on a scanned
 *    plane's trace; the inference engine snaps every point, digits typed go
 *    to the measurement box (Længde, Tab Vinkel), and Revit's temporary
 *    dimensions show the segment's length and its distance to the parallel
 *    walls either side. A chain is one batch (one undo);
 *  - a modify command from the selection bar (Flyt, Kopiér, Rotér, Spejl,
 *    Forskyd, Del, Saml hjørne, Forlæng / trim, Juster): its picks, its
 *    preview — the very batch a click commits, drawn in the accent — and
 *    its typed value.
 *
 * Listens on the Ground in the capture phase while a tool runs, so a left
 * click never reaches the camera or the selection; the plan still pans
 * with the right button.
 */
export function CadToolLayer({ viewport, host, editor, storeyId, z }: CadToolLayerProps) {
  const [, setTick] = useState(0);
  const layerRef = useRef<HTMLDivElement>(null);
  const [chain, setChainState] = useState<XY[]>([]);
  const [pointer, setPointerState] = useState<Pointer | null>(null);
  const [typed, setTypedState] = useState<Typed | null>(null);
  const [mods, setModsState] = useState<Mods>({ shift: false, alt: false });
  const [lock, setLock] = useState<Inference | null>(null);
  const live = useRef({ editor, storeyId, z, chain, pointer, typed, mods, lock });
  live.current = { editor, storeyId, z, chain, pointer, typed, mods, lock };
  const setChain = (c: XY[]) => {
    live.current.chain = c;
    setChainState(c);
  };
  const setTyped = (t: Typed | null) => {
    live.current.typed = t;
    setTypedState(t);
  };
  const setPointer = (p: Pointer | null) => {
    live.current.pointer = p;
    setPointerState(p);
  };

  const tool = editor.tool;
  const session = editor.modify;
  const wallTool = tool === 'wall';
  const active = (wallTool || session !== null) && editor.canEdit;
  const opts = editor.wallOptions;
  // Smart guides' hover-acquired tracking points: kept for the tool or command, afresh for the next.
  const { tracked, points: trackedNow, observe } = useSmartTracking(`${tool}:${session ? `${session.command}:${session.guids.join(',')}` : ''}:${opts.mode}`);

  // ------------------------------------------------------- projection --
  useEffect(() => {
    let frameId: number | null = null;
    const bump = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    bump();
    return () => {
      off();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [viewport]);

  const toScreen = useCallback(
    (p: XY): [number, number] | null => {
      const s = viewport.current?.projectWorld(new Float64Array([p[0], p[1], live.current.z]), { clip: false });
      return s && Number.isFinite(s[0]) && Number.isFinite(s[1]) ? [s[0], s[1]] : null;
    },
    [viewport],
  );
  const metresPer = useCallback(
    (px: number): number => {
      const a = viewport.current?.planPoint(0, 0, live.current.z);
      const b = viewport.current?.planPoint(px, 0, live.current.z);
      return a && b ? Math.hypot(b[0] - a[0], b[1] - a[1]) : 0.1;
    },
    [viewport],
  );

  // -------------------------------------------------- inference input --
  const model = editor.model;
  const snap = editor.snapTargets;
  const storeyWalls = useMemo(() => (model.walls ?? []).filter((w) => storeyId === null || w.storey_id === storeyId), [model.walls, storeyId]);
  const segments = useMemo<SnapSegment[]>(() => {
    const out: SnapSegment[] = storeyWalls.map((w) => ({ a: w.start, b: w.end, source: 'wall', label: `Væg ${w.id}`, guid: w.guid }));
    for (const t of snap?.plane_traces ?? []) out.push({ a: t.start, b: t.end, source: 'trace', label: `P-${t.plane_label}`, points: t.point_count });
    return out;
  }, [storeyWalls, snap]);
  const axisAngle = useMemo(() => manhattanAngle(snap?.axis_angles, segments), [snap, segments]);
  // The tape measure's guides (S9): lines and points every tool snaps to.
  const guides = useMemo(() => guideSnaps(editor.annotations.guides, storeyId), [editor.annotations.guides, storeyId]);
  const guidesRef = useRef(guides);
  guidesRef.current = guides;
  const storey = useMemo(() => model.storeys?.find((s) => s.id === storeyId) ?? null, [model.storeys, storeyId]);

  /** Where the inference measures from: the chain's last point, the base / pivot / first axis point. */
  const originOf = (s: ModifySession | null, c: XY[]): XY | null => {
    if (s) {
      if ((s.command === 'move' || s.command === 'copy' || s.command === 'mirror') && s.points.length === 1) return s.points[0];
      if (s.command === 'rotate' && s.points.length >= 1) return s.points[0];
      return null;
    }
    return c.length > 0 ? c[c.length - 1] : null;
  };

  /** Resolve the pointer at plan point `raw`: inference, wall under it, the line it holds to. */
  const resolve = useCallback(
    (raw: XY, m: Mods, lk: Inference | null): Pointer => {
      const { editor: ed, chain: c, storeyId: sid } = live.current;
      const s = ed.modify;
      // The chain's own segments snap too (its first point closes it).
      const own: SnapSegment[] = [];
      for (let i = 0; i + 1 < c.length; i += 1) own.push({ a: c[i], b: c[i + 1], source: 'wall', label: 'Ny væg' });
      if (c.length === 1) own.push({ a: c[0], b: c[0], source: 'wall', label: 'Startpunkt' });
      const tol = metresPer(SNAP_PX);
      const origin = originOf(s, c);
      let inf = infer(raw, {
        segments: [...segmentsRef.current, ...guidesRef.current.segments, ...own],
        points: guidesRef.current.points,
        axisAngle: axisRef.current,
        origin,
        toggles: ed.snap,
        tolerance: tol,
        suspended: m.alt,
        tracked: [...trackedNow(), ...toolTrackPoints(ed.model.walls ?? [], s, c, origin)],
      });
      observe(inf);
      if (m.shift && lk && (lk.guide || lk.segment)) {
        const [a, b] = lk.guide ?? [lk.segment!.a, lk.segment!.b];
        const d = sub(b, a);
        const L2 = dot(d, d);
        const t = L2 > 0 ? dot(sub(raw, a), d) / L2 : 0;
        inf = { ...lk, point: [a[0] + d[0] * t, a[1] + d[1] * t], tip: lk.tip.endsWith('låst') ? lk.tip : `${lk.tip} · låst` };
      }
      const walls = (ed.model.walls ?? []) as SolidWall[];
      // A pick reaches PICK_PX beyond a wall's faces, as a click does in Vælg.
      const hit = wallAt(walls, raw, sid, undefined, metresPer(PICK_PX)) ?? (inf.segment?.guid ? { wall: walls.find((w) => w.guid === inf.segment!.guid)!, t: 0 } : null);
      const seg = inf.segment;
      const line =
        seg && seg.a !== seg.b
          ? { a: seg.a, b: seg.b, source: seg.source, guid: seg.guid, label: seg.label }
          : hit?.wall
            ? { a: hit.wall.start, b: hit.wall.end, source: 'wall' as const, guid: hit.wall.guid, label: `Væg ${hit.wall.id}` }
            : null;
      return { raw, inf, pick: { p: inf.point, wall: hit?.wall?.guid ?? null, line } };
    },
    [metresPer, trackedNow, observe],
  );
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;
  const axisRef = useRef(axisAngle);
  axisRef.current = axisAngle;

  // ------------------------------------------------------ what to do --
  const drawCtx = useCallback(
    (): DrawContext => ({ walls: (live.current.editor.model.walls ?? []) as SolidWall[], storey, storeyId: live.current.storeyId, newGuid: live.current.editor.newGuid }),
    [storey],
  );

  /** The point the next click places for the wall tool: the pointer, or the typed length / angle from the last point. */
  const wallPoint = (c: XY[], p: Pointer | null, t: Typed | null): XY | null => {
    const at = p?.inf.point ?? null;
    if (c.length === 0 || !t) return at;
    const last = c[c.length - 1];
    const len = parseLength(t.fields[0] ?? '');
    const deg = t.fields[1] ? parseAngle(t.fields[1]) : null;
    if (len === null && deg !== null && at) {
      // Only the angle typed: the pointer's distance along it.
      return typedEndpoint(last, [1, 0], dist(last, at), deg);
    }
    if (len === null) return at;
    return typedEndpoint(last, at ? sub(at, last) : [1, 0], len, deg);
  };

  /** The wall tool's batch for the chain so far plus the pointer (the preview). */
  const wallPreview = (c: XY[], p: Pointer | null, t: Typed | null): { ops: ModelEditOp[]; walls: number } | null => {
    const o = live.current.editor.wallOptions;
    if (o.mode === 'trace') {
      const seg = p?.inf.segment;
      if (!p || !seg || seg.source !== 'trace') return null;
      const axis = traceAxis(seg.a, seg.b, p.raw, o, (live.current.editor.model.walls ?? []) as SolidWall[], live.current.storeyId);
      if (!axis) return null;
      const plan = chainPlan(axis, { ...o, placement: 'center' }, drawCtx());
      return { ops: plan.ops, walls: plan.walls.length };
    }
    if (o.mode === 'rectangle') {
      if (c.length !== 1 || !p) return null;
      const ring = rectanglePoints(c[0], p.inf.point, axisRef.current);
      if (!ring) return null;
      const plan = chainPlan(ring, o, drawCtx());
      return { ops: plan.ops, walls: plan.walls.length };
    }
    const next = wallPoint(c, p, t);
    const pts = next && c.length > 0 ? [...c, next] : c;
    if (pts.length < 2) return null;
    const plan = chainPlan(pts, o, drawCtx());
    return { ops: plan.ops, walls: plan.walls.length };
  };

  const modifyNow = (s: ModifySession, p: Pointer | null, t: Typed | null): ModifyPreview | null => {
    const ed = live.current.editor;
    // Previews make throwaway guids; the click makes its own.
    let n = 0;
    return modifyPreview(ed.model, s, p?.pick ?? null, t?.fields[0] || null, () => `preview-${++n}`);
  };

  // ---------------------------------------------------------- commits --
  const commitChain = useCallback(
    (pts: XY[]) => {
      const ed = live.current.editor;
      const plan = chainPlan(pts, ed.wallOptions, drawCtx());
      setChain([]);
      setTyped(null);
      setLock(null);
      setPointer(null);
      if (plan.ops.length === 0) return;
      const n = plan.walls.length;
      void ed.commit(plan.ops, n === 1 ? 'Ny væg' : `${n} nye vægge`);
    },
    [drawCtx],
  );

  const commitModify = (outcome: { session: ModifySession | null; commit: ModifyPreview | null }) => {
    const ed = live.current.editor;
    setTyped(null);
    setLock(null);
    if (outcome.commit) {
      if ('error' in outcome.commit.result) {
        ed.notify(outcome.commit.result.error);
        return;
      }
      if (outcome.commit.result.ops.length > 0) void ed.commit(outcome.commit.result.ops, outcome.commit.label);
      else ed.notify(`${outcome.commit.label}: intet at ændre`);
    }
    ed.setModify(outcome.session);
    if (outcome.commit) setPointer(null);
  };

  /** A click at the current pointer. */
  const click = useCallback(
    (p: Pointer) => {
      const { editor: ed, chain: c, typed: t } = live.current;
      const s = ed.modify;
      if (s) {
        commitModify(modifyClick(ed.model, s, p.pick, t?.fields[0] || null, ed.newGuid));
        return;
      }
      if (ed.tool !== 'wall') return;
      const o = ed.wallOptions;
      if (o.mode === 'trace') {
        const pv = wallPreview(c, p, t);
        if (!pv || pv.ops.length === 0) {
          ed.notify('Klik på et planspor (violet) for at sætte en væg på det');
          return;
        }
        void ed.commit(pv.ops, 'Væg fra planspor');
        return;
      }
      if (o.mode === 'rectangle') {
        if (c.length === 0) {
          setChain([p.inf.point]);
          return;
        }
        const ring = rectanglePoints(c[0], p.inf.point, axisRef.current);
        if (!ring) return;
        commitChain(ring);
        return;
      }
      const next = wallPoint(c, p, t);
      if (!next) return;
      setTyped(null);
      setLock(null);
      if (c.length > 0) {
        const last = c[c.length - 1];
        // A second click on the last point ends the chain.
        if (dist(next, last) <= metresPer(SAME_PX)) {
          commitChain(c);
          return;
        }
        const pts = [...c, next];
        if (isClosed(pts, 0.05)) {
          commitChain([...c, c[0]]);
          return;
        }
        if (!o.chain) {
          commitChain(pts);
          return;
        }
        setChain(pts);
        return;
      }
      setChain([next]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commitChain, metresPer],
  );

  // ------------------------------------------------- pointer listeners --
  useEffect(() => {
    const el = host.current;
    if (!el || !active) return;
    const planAt = (e: PointerEvent): XY | null => {
      const r = layerRef.current?.getBoundingClientRect();
      if (!r) return null;
      const hit = viewport.current?.planPoint(e.clientX - r.left, e.clientY - r.top, live.current.z);
      return hit ? [hit[0], hit[1]] : null;
    };
    const modsOf = (e: { shiftKey: boolean; altKey: boolean }): Mods => ({ shift: e.shiftKey, alt: e.altKey });
    const onMove = (e: PointerEvent) => {
      const raw = planAt(e);
      if (!raw) return;
      const m = modsOf(e);
      const cur = live.current;
      const lk = m.shift ? (cur.lock ?? cur.pointer?.inf ?? null) : null;
      if (lk !== cur.lock) setLock(lk);
      setModsState(m);
      setPointer(resolve(raw, m, lk));
      cur.editor.cursor.set(raw);
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || !(e.target instanceof HTMLCanvasElement)) return;
      e.stopPropagation();
      e.preventDefault();
      const raw = planAt(e);
      if (!raw) return;
      const m = modsOf(e);
      const p = resolve(raw, m, m.shift ? live.current.lock : null);
      setPointer(p);
      click(p);
    };
    el.addEventListener('pointermove', onMove, true);
    el.addEventListener('pointerdown', onDown, true);
    const prevCursor = el.style.cursor;
    el.style.cursor = 'crosshair';
    return () => {
      el.removeEventListener('pointermove', onMove, true);
      el.removeEventListener('pointerdown', onDown, true);
      el.style.cursor = prevCursor;
    };
  }, [host, viewport, active, resolve, click]);

  // A new tool or command starts afresh.
  const sessionKey = session ? `${session.command}:${session.guids.join(',')}` : '';
  useEffect(() => {
    setChain([]);
    setTyped(null);
    setLock(null);
    setPointer(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, sessionKey, opts.mode]);

  // ------------------------------------------------------------- keys --
  const gestureOn = active && (session !== null || chain.length > 0);
  useEffect(() => {
    if (!gestureOn) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null;
      // The options strip's fields keep their keys.
      if (target?.closest('input, textarea, select, [role="dialog"]')) return;
      const { editor: ed, chain: c, typed: t, pointer: p } = live.current;
      const s = ed.modify;
      const fields: readonly string[] = s ? (typedField(s) ? [typedField(s)!] : []) : ed.wallOptions.mode === 'line' ? WALL_FIELDS : [];
      const stop = () => {
        e.preventDefault();
        e.stopPropagation();
      };
      if (e.key === 'Escape') {
        stop();
        if (t) setTyped(null);
        else if (s) ed.setModify(modifyBack(s));
        else if (ed.wallOptions.mode === 'line' && c.length >= 2) commitChain(c);
        else setChain([]);
        return;
      }
      if (e.key === 'Enter') {
        stop();
        if (s) {
          commitModify(modifyEnter(ed.model, s, p?.pick ?? null, t?.fields[0] || null, ed.newGuid));
          return;
        }
        if (t && p) {
          // The typed point goes on the chain (Enter sets it, as a click would).
          click(p);
          return;
        }
        if (c.length >= 2) commitChain(c);
        return;
      }
      if (e.key === 'Tab') {
        stop();
        if (fields.length > 1) {
          const cur = t ?? { fields: [], at: 0 };
          setTyped({ ...cur, at: (cur.at + 1) % fields.length });
        }
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        stop();
        if (t) {
          const next = [...t.fields];
          next[t.at] = (next[t.at] ?? '').slice(0, -1);
          setTyped(next.every((x) => !x) ? null : { ...t, fields: next });
        } else if (!s && c.length > 0) setChain(c.slice(0, -1));
        return;
      }
      if (/^[0-9.,-]$/.test(e.key) && !e.metaKey && !e.ctrlKey && fields.length > 0) {
        stop();
        const cur = t ?? { fields: [], at: 0 };
        const next = [...cur.fields];
        next[cur.at] = (next[cur.at] ?? '') + e.key;
        setTyped({ ...cur, fields: next });
        return;
      }
      if (e.key === 'Shift' || e.key === 'Alt') {
        const m = { shift: e.shiftKey, alt: e.altKey };
        const lk = m.shift ? (live.current.lock ?? p?.inf ?? null) : null;
        setLock(lk);
        setModsState(m);
        if (p) setPointer(resolve(p.raw, m, lk));
        if (e.key === 'Alt') e.preventDefault();
        return;
      }
      // ⌘Z / ⇧⌘Z: the chain is not sent yet, so undo steps back a point.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !s && c.length > 0) {
        stop();
        setChain(c.slice(0, -1));
        return;
      }
      // Every other letter waits: no tool switch mid-gesture.
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey) stop();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 'Shift' && e.key !== 'Alt') return;
      const m = { shift: e.shiftKey, alt: e.altKey };
      const p = live.current.pointer;
      if (!m.shift) setLock(null);
      setModsState(m);
      if (p) setPointer(resolve(p.raw, m, m.shift ? live.current.lock : null));
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKeyUp, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gestureOn, resolve, click, commitChain]);

  // ------------------------------------------------------- the preview --
  const wallPv = active && wallTool && !session ? wallPreview(chain, pointer, typed) : null;
  const modPv = active && session ? modifyNow(session, pointer, typed) : null;
  const previewOps = wallPv?.ops ?? (modPv && 'ops' in modPv.result ? modPv.result.ops : []);
  const previewError = modPv && 'error' in modPv.result ? modPv.result.error : null;
  const after = useMemo(() => (previewOps.length > 0 ? applyOps(model, previewOps) : null), [model, previewOps]);
  // The walls the batch touches: new rows, or rows `applyOps` replaced (server ops included).
  const before = new Set(model.walls ?? []);
  const changed = after ? (after.walls ?? []).filter((w) => !before.has(w)) : [];

  // The segment being drawn: its length / angle, and its temporary dimensions.
  const drawing = wallTool && !session && opts.mode === 'line' && chain.length > 0 ? { from: chain[chain.length - 1], to: wallPoint(chain, pointer, typed) } : null;
  const lastNew = wallPv && after ? changed.filter((w) => !(model.walls ?? []).some((x) => x.guid === w.guid)).pop() : undefined;
  // The side distances a quarter along, clear of the length's figure.
  const lastFrame = lastNew ? frame(lastNew) : null;
  const dims: TempDim[] = (after && lastNew ? tempDims(after, lastNew.guid) : []).map((d) => {
    if (d.id === 'length' || !lastFrame) return d;
    const k = lastFrame.length / 4;
    const mv = (p: XY): XY => [p[0] + lastFrame.dir[0] * k, p[1] + lastFrame.dir[1] * k];
    return { ...d, from: mv(d.from), to: mv(d.to), at: mv(d.at) };
  });

  // The status line: hint and readout.
  const gesture = useMemo<GestureStatus | null>(() => {
    if (!active) return null;
    if (session) {
      const v = modPv?.value ?? null;
      const f = typedField(session);
      return { hint: modifyHint(session), readout: v !== null && f ? `${f} ${v}${f === 'Vinkel' ? '°' : ''}` : null };
    }
    if (chain.length === 0) return null;
    const keys = [
      ...(opts.mode === 'line' ? [{ key: '0–9', text: 'skriv længde' }, { key: 'Tab', text: 'vinkel' }] : []),
      { key: '⇧', text: 'lås retning' },
      { key: 'Alt', text: 'uden snap' },
      ...(opts.mode === 'line' ? [{ key: '⌫', text: 'fjern punkt' }] : []),
      { key: 'Esc', text: opts.mode === 'line' ? 'afslut kæde' : 'annullér' },
    ];
    const lead = opts.mode === 'rectangle' ? 'Væg · klik modsat hjørne' : 'Væg · klik næste punkt';
    const readout = drawing?.to ? `Længde ${fmt(dist(drawing.from, drawing.to))} · ${fmtDeg(angleOf(drawing.from, drawing.to))}` : null;
    return { hint: { lead, keys }, readout };
  }, [active, session, modPv?.value, chain.length, opts.mode, drawing?.from, drawing?.to]);
  useEffect(() => {
    editor.gesture.set(gesture);
  }, [editor.gesture, gesture]);
  useEffect(() => () => live.current.editor.gesture.set(null), []);

  if (!active) return <div ref={layerRef} className={styles.layer} />;

  // ------------------------------------------------------------- draw --
  const poly = (pts: XY[]): string | null => {
    const out: string[] = [];
    for (const p of pts) {
      const s = toScreen(p);
      if (!s) return null;
      out.push(`${s[0].toFixed(1)},${s[1].toFixed(1)}`);
    }
    return out.join(' ');
  };
  const outline = (w: SolidWall): string | null => {
    const f = frame(w);
    if (!f) return null;
    const h = Math.max((w.thickness ?? 0) / 2, 0.01);
    const n: XY = [f.normal[0] * h, f.normal[1] * h];
    return poly([
      [w.start[0] + n[0], w.start[1] + n[1]],
      [w.end[0] + n[0], w.end[1] + n[1]],
      [w.end[0] - n[0], w.end[1] - n[1]],
      [w.start[0] - n[0], w.start[1] - n[1]],
    ]);
  };

  // A pick (Del, Saml, Forlæng / trim, Juster) says what the click does instead of what the point snaps to.
  const picking = session !== null && (session.command === 'split' || session.command === 'join' || session.command === 'trim' || session.command === 'align' || session.command === 'offset');
  const inf = pointer && !typed && !picking ? pointer.inf : null;
  const infAt = inf && inf.kind !== 'free' ? toScreen(inf.point) : null;
  const guide = inf?.guide ? poly([inf.guide[0], inf.guide[1]]) : null;
  const pointerAt = pointer ? toScreen(drawing?.to ?? pointer.inf.point) : null;

  // The wall a modify command works against: the boundary (TR), the wall to align (AL), the first of a join (JN).
  const anchorWall = session && (session.command === 'trim' || session.command === 'align' || session.command === 'join') ? (model.walls ?? []).find((w) => w.guid === session.guids[0]) : undefined;
  let cutTip: string | null = null;
  const cut = session?.command === 'split' && pointer?.pick.wall ? (() => {
    const w = (model.walls ?? []).find((x) => x.guid === pointer.pick.wall);
    const p = w ? splitPoint(w, pointer.pick.p) : null;
    const f = w ? frame(w) : null;
    if (!w || !p || !f) return null;
    const a = dist(w.start, p);
    cutTip = `Del Væg ${w.id} · ${fmt(a)} + ${fmt(f.length - a)}`;
    const h = (w.thickness ?? 0.1) / 2 + 0.25;
    return poly([
      [p[0] + f.normal[0] * h, p[1] + f.normal[1] * h],
      [p[0] - f.normal[0] * h, p[1] - f.normal[1] * h],
    ]);
  })() : null;
  // What the pick will do, beside the pointer.
  const pickTip = previewError ? null : (cutTip ?? (picking && modPv && session?.command !== 'offset' ? modPv.label : null));
  // The line a hover would pick (Juster's reference, Fra planspor's trace).
  const hoverLine = (session?.command === 'align' || (wallTool && opts.mode === 'trace')) && pointer?.pick.line ? poly([pointer.pick.line.a, pointer.pick.line.b]) : null;

  // Measurement box: the wall tool's Længde / Vinkel, a modify command's value.
  const boxFields: readonly string[] = session ? (typedField(session) ? [typedField(session)!] : []) : drawing ? WALL_FIELDS : [];
  const boxLive: string[] = session
    ? [modPv?.value ?? '']
    : drawing?.to
      ? [fmt(dist(drawing.from, drawing.to)), angleOf(drawing.from, drawing.to).toFixed(1).replace('.', ',')]
      : [];
  const boxUnit = (field: string) => (field === 'Vinkel' ? '°' : ' m');

  return (
    <div ref={layerRef} className={styles.layer}>
      <svg className={styles.svg} aria-hidden="true">
        {guide && inf && (
          <polyline points={guide} className={inf.kind === 'trace' ? `${styles.guide} ${styles.traceGuide}` : styles.guide} style={{ stroke: `var(${INFERENCE_TOKEN[inf.kind]})` }} />
        )}
        {hoverLine && <polyline points={hoverLine} className={`${styles.guide} ${styles.traceGuide}`} style={{ stroke: 'var(--snap-trace)' }} />}
        {anchorWall && (() => {
          const d = outline(anchorWall);
          return d ? <polygon points={d} className={styles.anchor} /> : null;
        })()}
        {/* The command's guides: the drag from the base, the rotation's rays, the mirror axis. */}
        {modPv?.guides.map(([a, b], i) => {
          const d = poly([a, b]);
          return d ? <polyline key={i} points={d} className={styles.opGuide} /> : null;
        })}
        {/* What the click will make: the batch's walls, in the accent. */}
        {changed.map((w) => {
          const d = outline(w);
          return d ? <polygon key={w.guid} points={d} className={styles.preview} /> : null;
        })}
        {/* The chain clicked so far. */}
        {chain.length > 0 && (() => {
          const d = poly(drawing?.to ? [...chain, drawing.to] : chain);
          return d ? <polyline points={d} className={styles.chainLine} /> : null;
        })()}
        {chain.map((p, i) => {
          const s = toScreen(p);
          return s ? <circle key={i} cx={s[0]} cy={s[1]} r={3.5} className={styles.chainPoint} /> : null;
        })}
        {cut && <polyline points={cut} className={styles.cut} />}
        {dims.map((dim) => {
          const a = toScreen(dim.from);
          const b = toScreen(dim.to);
          if (!a || !b) return null;
          const dx = b[0] - a[0];
          const dy = b[1] - a[1];
          const L = Math.hypot(dx, dy) || 1;
          const nx = (-dy / L) * 4;
          const ny = (dx / L) * 4;
          return (
            <g key={dim.id} className={styles.dim}>
              <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />
              <line x1={a[0] - nx} y1={a[1] - ny} x2={a[0] + nx} y2={a[1] + ny} />
              <line x1={b[0] - nx} y1={b[1] - ny} x2={b[0] + nx} y2={b[1] + ny} />
            </g>
          );
        })}
        <TrackLines tracks={inf?.tracks} points={tracked} toScreen={toScreen} />
        {infAt && inf && <Glyph kind={inf.kind} at={infAt} />}
      </svg>
      {dims.map((dim) => {
        const at = toScreen(dim.at);
        if (!at) return null;
        return (
          <span
            key={dim.id}
            className={dim.id === 'length' ? styles.liveDim : styles.dimStatic}
            style={{ transform: `translate(${at[0].toFixed(1)}px, ${at[1].toFixed(1)}px) translate(-50%, -50%)` }}
          >
            {fmt(dim.value)}
          </span>
        );
      })}
      {infAt && inf?.tip && !previewError && !pickTip && <ScreenTip inf={inf} at={infAt} />}
      {pickTip && pointerAt && (
        <span className={styles.tip} style={{ transform: `translate(${(pointerAt[0] + 12).toFixed(1)}px, ${(pointerAt[1] - 30).toFixed(1)}px)` }}>
          {pickTip}
        </span>
      )}
      {previewError && pointerAt && (
        <span className={`${styles.tip} ${styles.tipWarn}`} style={{ transform: `translate(${(pointerAt[0] + 12).toFixed(1)}px, ${(pointerAt[1] - 30).toFixed(1)}px)` }}>
          {previewError}
        </span>
      )}
      {pointerAt && boxFields.length > 0 && (
        <span className={styles.measure} style={{ transform: `translate(${(pointerAt[0] + 16).toFixed(1)}px, ${(pointerAt[1] + 16).toFixed(1)}px)` }} role="status">
          <span className={styles.measureBox}>
            <small>{boxFields[typed?.at ?? 0]}</small>
            <b>
              {typed?.fields[typed.at] ? typed.fields[typed.at] : <span className={styles.measureLive}>{boxLive[typed?.at ?? 0] ?? ''}</span>}
              <u aria-hidden="true" />
              {boxUnit(boxFields[typed?.at ?? 0])}
            </b>
          </span>
          <span className={styles.measureCap}>
            Skriv et tal{boxFields.length > 1 ? ` · Tab ${boxFields[((typed?.at ?? 0) + 1) % boxFields.length].toLowerCase()}` : ''} · ↵ sæt
          </span>
        </span>
      )}
    </div>
  );
}
